import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { FormProps } from '@formio/react/lib/components/Form';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { Button } from '@components/ui';
import FullPageStepper from '@components/shared/FullPageStepper/FullPageStepper';
import LogoutButton from '@components/shared/LogoutButton/LogoutButton';
import { getFormConfigById } from '@/services/formService';
import { saveFormData, getFormData } from '@/services/formDataService';
import { requestEmployeeActivation } from '@/services/employeeService';
import { patchFileComponents, setCurrentWorkflow } from '@/lib/formioFileProvider';
import { useNotificationStore } from '@stores/notificationStore';
import { useTranslation } from '@/hooks/useTranslation';
import useFormioResize from '@hooks/useFormioResize';
import useMultiStepForm from '@/hooks/useMultiStepForm';
import { useEmployeeOnboardingForms } from './useEmployeeOnboardingForms';
import styles from './EmployeeOnboardingPage.module.scss';

const Form = lazy(() =>
  import('@formio/react').then((mod) => ({ default: mod.Form })),
);

// ============================================
// TYPES
// ============================================

interface FormioInstance {
  checkValidity: (data: Record<string, unknown>, dirty: boolean) => boolean;
}

// ============================================
// STEP FORM RENDERER
// ============================================

interface StepFormProps {
  formConfigId: string;
  objectType: string;
  objectId: string;
  onSaved: () => void;
  saveRef: React.MutableRefObject<(() => boolean) | null>;
}

function StepForm({ formConfigId, objectType, objectId, onSaved, saveRef }: StepFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const formRef = useRef<Record<string, unknown> | null>(null);
  const instanceRef = useRef<FormioInstance | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: formConfig, isLoading: configLoading, isError: configError } = useQuery({
    queryKey: ['formConfig', formConfigId],
    queryFn: () => getFormConfigById(formConfigId),
  });

  const { data: savedFormData, isLoading: dataLoading } = useQuery({
    queryKey: ['formData', objectType, objectId, formConfigId],
    queryFn: () => getFormData(objectType, objectId, formConfigId),
    enabled: !!objectType && !!objectId,
  });

  const submission = useMemo(() => {
    if (savedFormData?.data) {
      return { data: savedFormData.data } as Record<string, unknown>;
    }
    return undefined;
  }, [savedFormData]);

  const submitMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      saveFormData(objectType, objectId, formConfigId, { data, status: 'SUBMITTED' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['formData', objectType, objectId, formConfigId] });
      onSaved();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  useEffect(() => {
    setCurrentWorkflow('EMPLOYEE_FORM');
    return () => setCurrentWorkflow('');
  }, []);

  const patchedFormConfig = useMemo(() => {
    if (!formConfig?.formConfig) return null;
    return patchFileComponents(
      JSON.parse(JSON.stringify(formConfig.formConfig)) as Record<string, unknown>,
    );
  }, [formConfig?.formConfig]);

  useFormioResize(formConfig?.formConfig);

  const handleChange = useCallback((submission: { data: Record<string, unknown> }) => {
    formRef.current = submission.data;
  }, []);

  const handleFormReady = useCallback((instance: unknown) => {
    instanceRef.current = instance as FormioInstance;
  }, []);

  const handleSave = useCallback((): boolean => {
    const formData = formRef.current ?? {};
    const instance = instanceRef.current;
    if (instance) {
      const isValid = instance.checkValidity(formData, true);
      if (!isValid) {
        const errorEl = containerRef.current?.querySelector('.has-error, .formio-error-wrapper');
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }
    }
    submitMutation.mutate(formData);
    return true;
  }, [submitMutation]);

  saveRef.current = handleSave;

  const isLoading = configLoading || dataLoading;

  if (isLoading) {
    return <div className={styles.loading}>{t('K_LOADING_FORM', 'Loading form...')}</div>;
  }

  if (configError || !patchedFormConfig) {
    return <div className={styles.error}>{t('K_FAILED_LOAD_FORM', 'Failed to load form.')}</div>;
  }

  return (
    <div ref={containerRef}>
      <div className={styles.formRoot}>
        <Suspense fallback={<div className={styles.loading}>{t('K_LOADING_FORM', 'Loading form...')}</div>}>
          <Form
            src={patchedFormConfig as unknown as FormProps['src']}
            onChange={handleChange}
            formReady={handleFormReady}
            submission={submission as FormProps['submission']}
          />
        </Suspense>
      </div>
    </div>
  );
}

// ============================================
// PAGE COMPONENT
// ============================================

function EmployeeOnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const { employeeId, forms, isLoading, refetch } = useEmployeeOnboardingForms();

  const onboardingForms = forms;
  const totalSteps = onboardingForms.length;

  const saveRef = useRef<(() => boolean) | null>(null);
  const [savingStep, setSavingStep] = useState(false);

  const stepper = useMultiStepForm({
    totalSteps: Math.max(totalSteps, 1),
  });

  const currentForm = onboardingForms[stepper.currentStep];

  const steps = useMemo(
    () => onboardingForms.map((f) => ({
      id: f.formConfigId,
      label: f.formName,
      title: f.formName,
      saved: f.status === 'saved',
    })),
    [onboardingForms],
  );

  const handleStepSaved = useCallback(async () => {
    setSavingStep(false);
    refetch();
    if (stepper.isLastStep) {
      try {
        await requestEmployeeActivation(employeeId);
        void navigate({ to: '/e/employee-complete', replace: true });
      } catch (error) {
        const e = error as WorkpayApiError;
        addNotification({ variant: 'critical', title: t(e.description, e.description) });
      }
    } else {
      stepper.goToNext();
    }
  }, [stepper, refetch, navigate, employeeId, addNotification, t]);

  const handleContinue = useCallback(() => {
    if (saveRef.current) {
      const submitted = saveRef.current();
      if (submitted) {
        setSavingStep(true);
      }
    }
  }, []);

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <p>{t('K_LOADING_YOUR_FORMS', 'Loading your forms...')}</p>
      </div>
    );
  }

  // No forms assigned — go straight to complete page
  if (onboardingForms.length === 0) {
    void navigate({ to: '/e/employee-complete', replace: true });
    return null;
  }

  return (
    <FullPageStepper
      pageTitle={t('K_PAGE_TITLE_EMPLOYEE_ONBOARDING', 'Employee Onboarding')}
      steps={steps}
      activeStep={stepper.currentStep}
      hideCloseButton
      onClose={() => {}}
      onStepClick={(i) => stepper.goToStep(i)}
      headerAction={<LogoutButton />}
      footer={
        <div className={styles.footer}>
          <div />
          <div className={styles.footerRight}>
            {!stepper.isFirstStep && (
              <Button variant="outline" size="md" onClick={() => stepper.goToPrev()}>
                {t('K_BACK', 'Back')}
              </Button>
            )}
            <Button
              variant="filled"
              size="md"
              onClick={handleContinue}
              loading={savingStep}
            >
              {stepper.isLastStep
                ? t('K_BUTTON_SAVE', 'Save')
                : t('K_BUTTON_CONTINUE', 'Continue')}
            </Button>
          </div>
        </div>
      }
    >
      {currentForm && (
        <StepForm
          key={currentForm.formConfigId}
          formConfigId={currentForm.formConfigId}
          objectType="EMPLOYEE"
          objectId={employeeId}
          onSaved={handleStepSaved}
          saveRef={saveRef}
        />
      )}
    </FullPageStepper>
  );
}

export default EmployeeOnboardingPage;
